import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styles from "./NavBar.module.css";
import Image from "next/image";
import MobileSidebar from "./MobileSidebar";
import { handleLinkClick } from "@/Config/helper";
import CustomiseCta from "../CustomiseCta/CustomiseCta";

interface NavBarTwoProps {
  handleEnrollButtonClick: () => void;
  handleBroutcherButtonClick: (broutcherLink?: string) => void;
  pageTitle?: any;
  data?: any;
}

const NavTwo: React.FC<NavBarTwoProps> = ({
  handleBroutcherButtonClick,
  pageTitle,
  data,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  const feesBrochure = data?.fees_brouchure?.pdf_link?.url || "";
  const prospectusBrochure = data?.prospectous_brouchure?.pdf_link?.url || "";
  const isAlumniPage = router.pathname === "/alumni";

  // Check if the screen is mobile size
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    if (typeof window !== "undefined") {
      checkIfMobile();
      window.addEventListener("resize", checkIfMobile);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", checkIfMobile);
      }
    };
  }, []);

  const logoAlumniWidth = isAlumniPage
    ? {
        url: "/assets/alumni-logo.svg",
        alternativeText: "Regenesys Alumni Logo",
        width: isMobile ? 142 : 204,
        height: isMobile ? 34 : 49,
      }
    : {
        url: data?.logo?.url,
        alternativeText: data?.logo?.alternativeText || "Regenesys Logo",
        width: isMobile ? 142 : 204,
        height: isMobile ? 28 : 40,
      };
  const logo =
    router.pathname === "/alumni"
      ? {
          url: "/assets/Regenesys_Alumni_Logo_White.png",
          alternativeText: "Regenesys Alumni Logo",
        }
      : {
          url: data?.logo?.url,
          alternativeText: data?.logo?.alternativeText || "Regenesys Logo",
        };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleResize);
      handleResize();

      const handleRouteChange = () => {
        setDropdownOpen(null);
      };

      router.events.on("routeChangeStart", handleRouteChange);

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
        router.events.off("routeChangeStart", handleRouteChange);
      };
    }
  }, [router.events]);

  const handleMouseEnter = (index: number) => {
    setDropdownOpen(index);
  };

  const handleMouseLeave = () => {
    setDropdownOpen(null);
  };

  const isEventsPage =
    router.pathname === "/events" || router.pathname === "/events/[slug]";

  return (
    <>
      <div className={styles.mainNavContainer}>
        <div
          className={`${styles.navTwoContainer} ${
            scrolled ? styles.scrolled : ""
          }`}
          style={{
            backgroundColor: isEventsPage
              ? isMobile
                ? "#18463e"
                : "#fff"
              : pageTitle === "404 Error"
              ? "#18463e"
              : "none",
          }}
        >
          <div className={styles.navContent}>
            <div className={styles.logoContainer}>
              <Link href="/" title={logo.alternativeText}>
                <Image
                  title={logo.alternativeText}
                  src={
                    isEventsPage
                      ? isMobile
                        ? data?.logo?.url
                        : !scrolled
                        ? "/assets/regLogo.png" // Black logo for desktop on events page when not scrolled
                        : data?.logo?.url // Default logo when scrolled
                      : data?.logo?.url // Default logo for non-events pages
                  }
                  alt={logo.alternativeText}
                  width={204}
                  height={40}
                  className={styles.LogoImg}
                  style={{
                    width: logoAlumniWidth.width,
                    height: logoAlumniWidth.height,
                  }}
                />
              </Link>
            </div>

            <div className={styles.mobileApplyButton}>
              <CustomiseCta
                btnLabelDesktop="Study Enquiry"
                position="Top"
                ctaData={data?.study_enquiry_cta}
                preEnrollmentEnable
                mixpanelEvent="study_enquiry_clicked"
              />
            </div>

            <nav className={styles.navTwo}>
              {data?.main_nav_links?.map((item: any, index: number) => (
                <div
                  key={index}
                  className={`${styles.navItem} ${
                    item.nested_list_links?.length ? styles.hasSubmenu : ""
                  } `}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div onClick={() => setDropdownOpen(null)}>
                    <Link
                      href={item?.url}
                      className={
                        item?.label === "Apply"
                          ? styles.applyButton
                          : isEventsPage && !scrolled
                          ? styles.scrolledNavLinkTwo
                          : styles.navLinkTwo
                      }
                      aria-label={item?.label}
                      title={item?.label}
                    >
                      {item?.label}
                      {item?.nested_list_links?.length > 0 && (
                        <Image
                          src={
                            isEventsPage && !scrolled
                              ? "/assets/DropDownArrow_black.svg"
                              : "/assets/DropDownArrow.svg"
                          }
                          alt="DropDown Arrow"
                          title="DropDown Arrow"
                          width={12}
                          height={12}
                          className={`${styles.dropDownImg} ${
                            dropdownOpen === index ? styles.rotateArrow : ""
                          }`}
                        />
                      )}
                    </Link>
                  </div>

                  {item.nested_list_links?.length > 0 &&
                    dropdownOpen === index && (
                      <div
                        className={
                          item.label === "Study at Regenesys"
                            ? styles.submenu
                            : styles.submenuDropDown
                        }
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className={styles.submenuItems}>
                          <div
                            className={
                              item.label === "Study at Regenesys"
                                ? styles.megamenuFlex
                                : styles.megamenuFlexDropDown
                            }
                            style={{
                              display:
                                item.label === "Study at Regenesys"
                                  ? "grid"
                                  : "block",
                            }}
                          >
                            {item.nested_list_links.map(
                              (subItem: any, subIndex: number) => (
                                <div
                                  key={subIndex}
                                  className={styles.submenuSection}
                                >
                                  {subItem?.title === "Fees" ||
                                  subItem?.title === "Prospectus" ? (
                                    <button
                                      onClick={() => {
                                        handleBroutcherButtonClick(
                                          subItem?.title === "Fees"
                                            ? feesBrochure
                                            : prospectusBrochure
                                        );
                                        setDropdownOpen(null);
                                      }}
                                      className={styles.submenuTitleButton}
                                    >
                                      {subItem?.title}
                                    </button>
                                  ) : (
                                    <div onClick={() => setDropdownOpen(null)}>
                                      <Link
                                        href={subItem?.url}
                                        title={subItem?.title}
                                        className={styles.submenuTitle}
                                        onClick={(e) =>
                                          handleLinkClick(
                                            e,
                                            subItem?.url,
                                            router
                                          )
                                        }
                                      >
                                        <h3>{subItem?.title}</h3>
                                      </Link>
                                    </div>
                                  )}

                                  {subItem.lists &&
                                    subItem.lists.map(
                                      (section: any, sectionIndex: number) => (
                                        <div
                                          key={sectionIndex}
                                          className={styles.sectionMenuItems}
                                        >
                                          <Link
                                            href={section?.url}
                                            className={styles.sectionTitle}
                                            title={section?.title}
                                            onClick={() =>
                                              setDropdownOpen(null)
                                            }
                                          >
                                            <h4>{section?.title}</h4>
                                          </Link>
                                          {section?.items.map(
                                            (item: any, itemIndex: number) => (
                                              <Link
                                                key={itemIndex}
                                                href={item?.url}
                                                className={styles.submenuItem}
                                                title={item?.label}
                                                onClick={() =>
                                                  setDropdownOpen(null)
                                                }
                                              >
                                                {item?.label}
                                              </Link>
                                            )
                                          )}
                                        </div>
                                      )
                                    )}
                                  {subItem.links &&
                                    subItem.links.map(
                                      (linkItem: any, linkIndex: number) => (
                                        <Link
                                          key={linkIndex}
                                          href={linkItem?.url}
                                          title={linkItem?.label}
                                          className={styles.submenuItem}
                                          onClick={() => setDropdownOpen(null)}
                                        >
                                          {linkItem?.label}
                                        </Link>
                                      )
                                    )}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              ))}

              <div className={styles.navItem}>
                <div
                  className={styles.applyButton}
                  data-interaction="Nav CTA Click"
                >
                  <CustomiseCta
                    btnLabelDesktop="Study Enquiry"
                    position="Top"
                    ctaData={data?.study_enquiry_cta}
                    preEnrollmentEnable
                    mixpanelEvent="study_enquiry_clicked"
                  />
                </div>
              </div>
            </nav>
          </div>
        </div>
        <MobileSidebar
          apiData={data?.main_nav_links}
          data={data}
          handleBroutcherButtonClick={handleBroutcherButtonClick}
          feesBrochure={feesBrochure}
          prospectusBrochure={prospectusBrochure}
        />
      </div>
    </>
  );
};

export default NavTwo;
